(function () {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;

  // Ensure utils is loaded before proceeding
  if (typeof window.requireUtils !== 'function') {
    console.error(
      '[cart-subscription.js] window.requireUtils is not defined. utils.js may not have loaded.'
    );
    return;
  }

  try {
    window.requireUtils('cart-subscription.js');
  } catch (e) {
    console.error('[cart-subscription.js] Dependency check failed:', e.message);
    return; // Fail silently if utils not available (prevents further errors)
  }

  const cartItemsContainer = document.getElementById('cart-drawer-items');
  if (!cartItemsContainer) return;

  const productCache = new Map();
  let ckSubsHydrateScheduled = false;
  const ckSubsRetryCount = new Map();
  const MAX_RETRIES = 10;
  // Track active sync timeouts to allow cleanup
  const activeSyncTimeouts = new Map();
  // Track pending subscription changes (waiting for Appstle events)
  const pendingSubscriptionChanges = new Map();

  function getProductHandleFromItem(item) {
    return window.utils.getProductHandleFromItem(item);
  }

  async function fetchProductJson(handle) {
    if (!handle) return null;
    if (productCache.has(handle)) return productCache.get(handle);

    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) {
        console.error(
          `[cart-subscription.js] Failed to fetch product ${handle}:`,
          res.status,
          res.statusText
        );
        return null;
      }
      const product = await res.json();
      productCache.set(handle, product);
      return product;
    } catch (e) {
      console.error(`[cart-subscription.js] Error fetching product ${handle}:`, e);
      return null;
    }
  }

  function getSellingPlanIdFromCartItem(item) {
    return window.utils.getSellingPlanIdFromCartItem(item);
  }

  function scheduleHydrateSubscriptionSelects(host = null) {
    if (ckSubsHydrateScheduled) return;
    ckSubsHydrateScheduled = true;

    if (host) {
      const retries = (ckSubsRetryCount.get(host) || 0) + 1;
      if (retries > MAX_RETRIES) {
        ckSubsRetryCount.delete(host);
        ckSubsHydrateScheduled = false;
        return;
      }
      ckSubsRetryCount.set(host, retries);
    }

    requestAnimationFrame(() => {
      ckSubsHydrateScheduled = false;
      hydrateSubscriptionSelects();
    });
  }

  /**
   * Ensure Appstle widget exists and is initialized
   * Appstle needs the widget to be visible (at least temporarily) to initialize it
   */
  function ensureAppstleWidget(cartItemEl, productId, variantId) {
    if (!cartItemEl) return null;

    let widget = cartItemEl.querySelector('.appstle_sub_widget');
    if (widget) {
      // Widget exists - check if it has been initialized (has inputs)
      const hasInputs = widget.querySelectorAll('input').length > 0;
      if (hasInputs) {
        return widget; // Already initialized
      }
      // Widget exists but not initialized - try to initialize it
    } else {
      // Create new widget container
      widget = document.createElement('div');
      widget.className = 'appstle_sub_widget';
      widget.setAttribute('data-product-id', productId);
      if (variantId) widget.setAttribute('data-variant-id', variantId);

      const selectEl = cartItemEl.querySelector('select.ck-cart-subscription-select');
      const parent = selectEl?.parentElement || cartItemEl;
      parent.insertBefore(widget, selectEl || null);
    }

    // Make widget visible temporarily so Appstle can detect and initialize it
    // Appstle's script scans for visible widgets on initialization
    const wasHidden = widget.style.display === 'none';
    if (wasHidden) {
      widget.style.display = 'block';
      widget.style.position = 'absolute';
      widget.style.left = '-9999px';
      widget.style.width = '1px';
      widget.style.height = '1px';
      widget.style.overflow = 'hidden';
    }

    // Try to trigger Appstle initialization
    // Check if Appstle has initialization functions available
    if (typeof window.appstleInit === 'function') {
      try {
        window.appstleInit();
      } catch (err) {
        console.warn('[cart-subscription.js] appstleInit failed:', err);
      }
    }

    // Check for Appstle's force render function
    if (typeof window.forceRenderWithProductHandle === 'function') {
      try {
        // Get product handle from cart item
        const handleEl = cartItemEl.querySelector('[data-product-handle]');
        const handle = handleEl?.getAttribute('data-product-handle');
        if (handle) {
          window.forceRenderWithProductHandle(handle);
        }
      } catch (err) {
        console.warn('[cart-subscription.js] forceRenderWithProductHandle failed:', err);
      }
    }

    // Wait a bit for Appstle to initialize, then hide again
    setTimeout(() => {
      const hasInputs = widget.querySelectorAll('input').length > 0;

      if (wasHidden) {
        widget.style.display = 'none';
        widget.style.position = '';
        widget.style.left = '';
        widget.style.width = '';
        widget.style.height = '';
        widget.style.overflow = '';
      }
    }, 1000); // Wait 1 second for Appstle to initialize

    return widget;
  }

  function getCartItemElements(source) {
    const widget =
      source?.closest?.('.appstle_sub_widget') ||
      (source?.querySelector ? source.querySelector('.appstle_sub_widget') : null);
    if (!widget) return null;

    const cartItemEl = widget.closest('[data-cart-item-key]');
    if (!cartItemEl) return null;

    const ckSelect = cartItemEl.querySelector('select.ck-cart-subscription-select');
    if (!ckSelect) return null;

    return { widget, cartItemEl, ckSelect };
  }

  function triggerChange(el) {
    if (el) {
      // Try multiple event types that Appstle might be listening to
      const events = ['change', 'input', 'click'];
      events.forEach((eventType) => {
        const event = new Event(eventType, { bubbles: true, cancelable: true });
        el.dispatchEvent(event);
      });
    }
  }

  /**
   * Sync Appstle widget OR directly update Shopify cart
   * If Appstle widget is not initialized, we update Shopify cart directly
   * Appstle will sync with Shopify's cart state automatically
   * @returns {Promise<string>} The line item key (may change after cart update)
   */
  async function syncAppstleWidgetFromSelect(cartItemEl, sellingPlanId) {
    let lineItemKey = cartItemEl
      ?.querySelector('[data-cart-item-key]')
      ?.getAttribute('data-cart-item-key');
    const quantityInput = cartItemEl?.querySelector('input[data-cart-item-key]');
    const quantity = parseInt(quantityInput?.value || '1', 10) || 1;

    const widget = cartItemEl?.querySelector('.appstle_sub_widget');
    const appstleInput = widget?.querySelector('input[name="selling_plan"]');
    const hasAppstleWidget = appstleInput !== null;

    // If Appstle widget is initialized, use it
    if (hasAppstleWidget) {
      const isOneTime = !sellingPlanId || sellingPlanId === 'onetime';
      appstleInput.value = isOneTime ? '' : sellingPlanId;

      const targetInput = isOneTime
        ? widget.querySelector('input[type="radio"][data-appstle-type="onetime"]') ||
          widget.querySelector('input[name="appstle_one_time_purchase"]')
        : widget.querySelector(`input[name="selling_plan"][value="${sellingPlanId}"]`);

      if (targetInput && targetInput.type === 'radio') {
        targetInput.checked = true;
        triggerChange(targetInput);
      } else {
        triggerChange(appstleInput);
      }
      // Return the original line item key (Appstle widget updates don't change it immediately)
      return lineItemKey;
    }

    // Appstle widget not initialized - update Shopify cart directly
    // Appstle will sync with Shopify's cart state automatically
    if (!lineItemKey) {
      return;
    }

    try {
      // Get cart change URL from cart drawer or use default
      // Shopify Cart API requires .js extension for JSON responses
      const cartDrawer = document.getElementById('cart-drawer');
      let cartChangeUrl = cartDrawer?.getAttribute('data-cart-change-url') || '/cart/change.js';
      // Ensure .js extension is present for JSON response
      if (!cartChangeUrl.endsWith('.js')) {
        cartChangeUrl = cartChangeUrl + '.js';
      }

      const sellingPlan = sellingPlanId === 'onetime' ? null : sellingPlanId;
      const payload = {
        id: lineItemKey,
        quantity,
        selling_plan: sellingPlan, // null => one-time
      };

      const response = await fetch(cartChangeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
      }

      // Shopify Cart API (.js endpoints) returns text/javascript, not application/json
      // Check for valid JSON content types
      const contentType = response.headers.get('content-type') || '';
      const isJson =
        contentType.includes('application/json') ||
        contentType.includes('text/javascript') ||
        contentType.includes('application/javascript');

      let cart;
      if (isJson) {
        // Parse as JSON - Shopify .js endpoints return valid JSON with text/javascript content-type
        cart = await response.json();
      } else {
        // Content-type suggests non-JSON, but try to parse anyway (might be HTML error page)
        const text = await response.text();
        try {
          cart = JSON.parse(text);
        } catch (parseError) {
          // Not JSON - likely HTML error page
          console.error('[cart-subscription.js] Shopify cart API returned non-JSON response:', {
            status: response.status,
            statusText: response.statusText,
            contentType,
            preview: text.substring(0, 200),
          });
          throw new Error(
            `Cart API returned non-JSON response: ${response.status} ${response.statusText}`
          );
        }
      }
      const updatedItem = cart.items?.find((item) => item.key === lineItemKey);
      const newLineItemKey = updatedItem?.key;
      const appliedPlanId = updatedItem?.selling_plan_allocation?.selling_plan?.id
        ? String(updatedItem.selling_plan_allocation.selling_plan.id)
        : null;

      // Update select value immediately before cart refresh
      // This ensures the dropdown shows the correct value
      const expectedPlanId = sellingPlanId === 'onetime' ? null : sellingPlanId;
      const selectValue = expectedPlanId || 'onetime';

      const select = cartItemEl?.querySelector('select.ck-cart-subscription-select');
      if (select) {
        select.value = selectValue;
        if (expectedPlanId) {
          select.dataset.currentPlanId = String(expectedPlanId);
        } else {
          select.dataset.currentPlanId = '';
        }
      }

      // Note: Shopify may change the line item key when subscription changes
      // If the key changed, update our pending changes and timeout maps
      if (newLineItemKey && newLineItemKey !== lineItemKey) {
        // Update pending changes map with new key
        const pending = pendingSubscriptionChanges.get(lineItemKey);
        if (pending) {
          pendingSubscriptionChanges.set(newLineItemKey, pending);
          pendingSubscriptionChanges.delete(lineItemKey);
        }
        // Update timeout maps
        const timeoutId = activeSyncTimeouts.get(lineItemKey);
        if (timeoutId) {
          activeSyncTimeouts.set(newLineItemKey, timeoutId);
          activeSyncTimeouts.delete(lineItemKey);
        }
        const fallbackTimeoutId = activeSyncTimeouts.get(`${lineItemKey}_fallback`);
        if (fallbackTimeoutId) {
          activeSyncTimeouts.set(`${newLineItemKey}_fallback`, fallbackTimeoutId);
          activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
        }
        // Update select's data attribute
        if (select) {
          select.dataset.cartItemKey = newLineItemKey;
        }
        // Update the lineItemKey variable for return value
        lineItemKey = newLineItemKey;
      }

      // Clear pending change and timeouts IMMEDIATELY after successful cart update
      // This prevents the fallback timeout from running
      // Do this BEFORE calling refreshCartAfterSubscriptionChange to avoid race conditions
      let pending = pendingSubscriptionChanges.get(lineItemKey);
      if (!pending && newLineItemKey && newLineItemKey !== lineItemKey) {
        // If key changed, try to get pending from new key
        pending = pendingSubscriptionChanges.get(newLineItemKey);
      }

      if (pending) {
        // Clear all timeouts
        const timeoutId = activeSyncTimeouts.get(lineItemKey);
        const fallbackTimeoutId = activeSyncTimeouts.get(`${lineItemKey}_fallback`);
        if (timeoutId) clearTimeout(timeoutId);
        if (fallbackTimeoutId) clearTimeout(fallbackTimeoutId);
        activeSyncTimeouts.delete(lineItemKey);
        activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
        if (newLineItemKey && newLineItemKey !== lineItemKey) {
          activeSyncTimeouts.delete(newLineItemKey);
          activeSyncTimeouts.delete(`${newLineItemKey}_fallback`);
        }

        // Remove from pending changes
        pendingSubscriptionChanges.delete(lineItemKey);
        if (newLineItemKey && newLineItemKey !== lineItemKey) {
          pendingSubscriptionChanges.delete(newLineItemKey);
        }

        // Remove loading state from select (but keep the value we just set)
        pending.select.disabled = false;
        delete pending.select.dataset.loading;
        const spinner = pending.select.parentElement?.querySelector(
          'ck-spinner[data-subscription-spinner]'
        );
        if (spinner) spinner.remove();
      }

      // Refresh cart UI immediately with the correct plan ID
      // This will update the UI and sync the select value from the cart data
      if (window.refreshCartAfterSubscriptionChange) {
        window.refreshCartAfterSubscriptionChange(lineItemKey, expectedPlanId || 'onetime');
      }

      // Return the (possibly updated) line item key so caller can use it
      return lineItemKey;
    } catch (error) {
      console.error('[cart-subscription.js] Error updating Shopify cart:', error);
      throw error; // Re-throw so handleSubscriptionChange can handle it
    }
  }

  function updateCkSelectValue(selectEl, value) {
    if (!selectEl || selectEl.value === value) return;

    selectEl.value = value;
    if (value === 'onetime') {
      selectEl.removeAttribute('data-current-plan-id');
    } else {
      selectEl.dataset.currentPlanId = value;
    }
  }

  async function hydrateSubscriptionSelects() {
    const hosts = cartItemsContainer.querySelectorAll('select.ck-cart-subscription-select');
    if (!hosts.length) return;

    try {
      for (const host of hosts) {
        const lineItemKey = host.dataset.cartItemKey;
        const handle = host.dataset.productHandle;

        // Skip if already syncing to prevent duplicate calls and infinite loops
        if (host.dataset.syncing === 'true') {
          continue;
        }

        // Clear any pending sync timeout for this item if it exists
        if (lineItemKey && activeSyncTimeouts.has(lineItemKey)) {
          clearTimeout(activeSyncTimeouts.get(lineItemKey));
          activeSyncTimeouts.delete(lineItemKey);
        }

        if (!handle) continue;

        const cartItemEl = lineItemKey
          ? cartItemsContainer.querySelector(`[data-cart-item-key="${lineItemKey}"]`)
          : null;

        // Clear any pending sync timeout for this item if element was removed
        if (!cartItemEl && lineItemKey && activeSyncTimeouts.has(lineItemKey)) {
          clearTimeout(activeSyncTimeouts.get(lineItemKey));
          activeSyncTimeouts.delete(lineItemKey);
        }

        const currentPlanId = window.utils.normalizeStringValue(host.dataset.currentPlanId);

        const product = await fetchProductJson(handle);
        const groups = product?.selling_plan_groups || [];

        if (!groups.length) {
          host.style.display = 'none';
          if (cartItemEl) {
            const widget = cartItemEl.querySelector('.appstle_sub_widget');
            if (widget) widget.remove();
          }
          continue;
        } else {
          host.style.removeProperty('display');
        }

        if (cartItemEl && product?.id) {
          const variantId = cartItemEl
            .querySelector('input[data-cart-item-id]')
            ?.getAttribute('data-cart-item-id');
          ensureAppstleWidget(cartItemEl, String(product.id), variantId || null);
        }

        const innerSelect = host;

        if (!innerSelect) {
          scheduleHydrateSubscriptionSelects(host);
          continue;
        }

        const signature =
          `${handle}::` +
          groups.map((g) => (g.selling_plans || []).map((p) => p.id).join(',')).join('|');

        if (host.dataset.ckSubsSignature !== signature) {
          host.dataset.ckSubsSignature = signature;
          host.innerHTML = '';

          const oneTimeOpt = document.createElement('option');
          oneTimeOpt.value = 'onetime';
          oneTimeOpt.textContent = 'One time';
          host.appendChild(oneTimeOpt);

          // Build options - don't preselect anything here
          // Selection will be determined by the "desired" value logic below
          for (const group of groups) {
            for (const plan of group.selling_plans || []) {
              const opt = document.createElement('option');
              opt.value = String(plan.id);
              opt.textContent = plan.name;
              // Don't set selected here - let the desired value logic handle it
              host.appendChild(opt);
            }
          }
        }

        // Determine desired selection based on cart item's actual subscription state
        // KEY LOGIC:
        // - If cart item has NO selling_plan_allocation (currentPlanId is empty),
        //   user chose "onetime" from PDP → default to "onetime"
        // - If cart item HAS selling_plan_allocation (currentPlanId exists),
        //   user chose "Subscribe and Save" from PDP → use that plan ID
        const userHasChosen = host.dataset.synced === 'true';
        const currentSelectValue = host.value;

        // Determine desired value based on cart item's actual state
        let desired = currentPlanId || null;

        // Check localStorage as fallback if cart item doesn't have subscription
        // This handles cases where subscription was selected on PDP but Shopify hasn't applied it yet
        if (!desired && !userHasChosen) {
          try {
            const cartItemEl = lineItemKey
              ? cartItemsContainer.querySelector(`[data-cart-item-key="${lineItemKey}"]`)
              : null;
            const variantId = cartItemEl
              ?.querySelector('input[data-cart-item-id]')
              ?.getAttribute('data-cart-item-id');

            if (variantId) {
              const storageKey = `subscription_plan_${variantId}`;
              const storedPlanId = localStorage.getItem(storageKey);
              if (storedPlanId && storedPlanId !== 'null' && storedPlanId !== '') {
                desired = storedPlanId;
                // Clear localStorage after using it (one-time use)
                localStorage.removeItem(storageKey);
              }
            }
          } catch (e) {
            // localStorage might be disabled, silently fail
          }
        }

        if (userHasChosen) {
          // User has explicitly synced in cart - respect their choice
          if (currentSelectValue === 'onetime' || !currentPlanId) {
            desired = 'onetime';
          } else {
            desired =
              currentSelectValue !== 'onetime' ? currentSelectValue : currentPlanId || 'onetime';
          }
        } else if (!desired) {
          // User hasn't synced in cart yet - check cart item's actual state from Shopify
          // If currentPlanId is empty and no localStorage, it means cart item has NO subscription
          // This means user chose "onetime" from PDP → default to "onetime"
          desired = 'onetime';
        }

        if (host.value !== desired) {
          host.value = desired;
          host.dataset.currentPlanId = desired !== 'onetime' ? desired : '';

          // If we used localStorage fallback, the subscription might not be synced to Shopify yet
          // Trigger sync to ensure Shopify cart matches the UI
          if (!currentPlanId && desired !== 'onetime' && cartItemEl) {
            // Small delay to ensure DOM is updated, then sync
            setTimeout(() => {
              syncAppstleWidgetFromSelect(cartItemEl, desired);
            }, 100);
          }
        }

        // Only sync Appstle widget on initial load if needed
        // User-initiated changes are handled by handleSubscriptionChange
        // During hydration, we only sync if Appstle widget exists and is initialized
        // We DON'T call Shopify Cart API during hydration - that's only for user-initiated changes
        if (cartItemEl && !userHasChosen && !host.dataset.loading) {
          const widget = cartItemEl.querySelector('.appstle_sub_widget');
          const hasAppstleWidget = widget?.querySelector('input[name="selling_plan"]') !== null;
          // Only sync if Appstle widget is initialized - don't call Shopify API during hydration
          if (hasAppstleWidget) {
            syncAppstleWidgetFromSelect(cartItemEl, desired);
          }
        }
      }
    } catch (error) {
      console.error('[cart-subscription.js] Error in hydrateSubscriptionSelects:', error);
    }
  }

  function handleAppstleWidgetInit(event) {
    const elements = getCartItemElements(event.target);
    if (!elements) {
      return;
    }

    const currentPlanId = elements.ckSelect.dataset.currentPlanId || '';
    // Use the selected value from the select, or first subscription plan if available
    const selectedValue = elements.ckSelect.value;
    const valueToUse =
      selectedValue && selectedValue !== 'onetime' ? selectedValue : currentPlanId || '';

    // During widget initialization, only sync if widget exists
    // Don't call Shopify Cart API during initialization
    const widget = elements.cartItemEl?.querySelector('.appstle_sub_widget');
    const hasAppstleWidget = widget?.querySelector('input[name="selling_plan"]') !== null;
    if (hasAppstleWidget) {
      syncAppstleWidgetFromSelect(elements.cartItemEl, valueToUse || 'onetime');
    }
  }

  /**
   * Handle subscription change initiated by user
   * Syncs Appstle widget and waits for Appstle events to confirm update
   * Falls back to cart refresh if Appstle events don't fire
   */
  async function handleSubscriptionChange(cartItemEl, select, newValue, previousValue) {
    const lineItemKey = select.dataset.cartItemKey;
    if (!lineItemKey) return;

    // Store pending change info
    pendingSubscriptionChanges.set(lineItemKey, {
      select,
      newValue,
      previousValue,
      timestamp: Date.now(),
    });

    try {
      // syncAppstleWidgetFromSelect returns the (possibly updated) line item key
      const updatedLineItemKey = await syncAppstleWidgetFromSelect(cartItemEl, newValue);

      // Update pending changes map if key changed
      if (updatedLineItemKey && updatedLineItemKey !== lineItemKey) {
        const pending = pendingSubscriptionChanges.get(lineItemKey);
        if (pending) {
          pendingSubscriptionChanges.set(updatedLineItemKey, pending);
          pendingSubscriptionChanges.delete(lineItemKey);
        }
        lineItemKey = updatedLineItemKey;
      }
    } catch (error) {
      console.error('[cart-subscription.js] Error syncing subscription:', error);
      // On error, revert and show error message
      select.value = previousValue || 'onetime';
      select.disabled = false;
      delete select.dataset.loading;
      const spinner = select.parentElement?.querySelector('ck-spinner[data-subscription-spinner]');
      if (spinner) spinner.remove();
      pendingSubscriptionChanges.delete(lineItemKey);
      clearTimeout(activeSyncTimeouts.get(lineItemKey));
      clearTimeout(activeSyncTimeouts.get(`${lineItemKey}_fallback`));
      activeSyncTimeouts.delete(lineItemKey);
      activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
      window.ckToast?.error('subscription', 'Failed to update subscription', {
        title: 'Could not update subscription',
      });
      return;
    }

    // Set a shorter timeout for fallback check (2 seconds)
    // If Appstle events don't fire, we'll check the cart state directly
    // Note: If we updated Shopify directly (Appstle widget not initialized),
    // Appstle won't fire events, so we need to check cart state anyway
    const fallbackTimeoutId = setTimeout(async () => {
      const pending = pendingSubscriptionChanges.get(lineItemKey);
      if (!pending) {
        return; // Already handled by Appstle event
      }

      // Check cart state to see if update was successful
      // Note: Shopify may change line item keys after subscription changes
      // Try both the original key and search by variant_id + properties
      try {
        // Use checkout.js fetchCart function if available
        if (!window.fetchCart) {
          throw new Error('fetchCart function not available');
        }
        const cart = await window.fetchCart();
        if (cart) {
          // Try to find the item by original key first
          let updatedItem = cart.items?.find((item) => item.key === lineItemKey);

          // If not found, try to find by variant_id and properties (Shopify may have changed the key)
          if (!updatedItem && pending) {
            const cartItemEl = pending.select.closest('[data-cart-item-key]');
            const variantId = cartItemEl
              ?.querySelector('[data-cart-item-id]')
              ?.getAttribute('data-cart-item-id');
            if (variantId) {
              // Find by variant_id - there should only be one matching item
              updatedItem = cart.items?.find((item) => String(item.variant_id) === variantId);
              if (updatedItem) {
                // Update pending changes with new key
                pendingSubscriptionChanges.set(updatedItem.key, pending);
                pendingSubscriptionChanges.delete(lineItemKey);
                lineItemKey = updatedItem.key;
                pending.select.dataset.cartItemKey = updatedItem.key;
              }
            }
          }

          const appliedPlanId = updatedItem?.selling_plan_allocation?.selling_plan?.id
            ? String(updatedItem.selling_plan_allocation.selling_plan.id)
            : null;

          const expectedPlanId = newValue === 'onetime' ? null : newValue;

          // If cart state matches what we expected, treat as success
          if (
            (expectedPlanId === null && appliedPlanId === null) ||
            (expectedPlanId && appliedPlanId === expectedPlanId)
          ) {
            // Success - Appstle updated cart but didn't fire event
            clearTimeout(activeSyncTimeouts.get(lineItemKey));
            clearTimeout(activeSyncTimeouts.get(`${lineItemKey}_fallback`));
            activeSyncTimeouts.delete(lineItemKey);
            activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
            pendingSubscriptionChanges.delete(lineItemKey);

            if (window.refreshCartAfterSubscriptionChange) {
              window.refreshCartAfterSubscriptionChange(lineItemKey, expectedPlanId || 'onetime');
            }
            return;
          }
        }
      } catch (err) {
        console.error('[cart-subscription.js] Error checking cart state:', err);
      }

      // Fallback failed - revert and show error
      pending.select.value = pending.previousValue || 'onetime';
      pending.select.disabled = false;
      delete pending.select.dataset.loading;

      // Remove spinner
      const spinner = pending.select.parentElement?.querySelector(
        'ck-spinner[data-subscription-spinner]'
      );
      if (spinner) spinner.remove();

      pendingSubscriptionChanges.delete(lineItemKey);
      clearTimeout(activeSyncTimeouts.get(lineItemKey));
      activeSyncTimeouts.delete(lineItemKey);

      window.ckToast?.error('subscription', 'Subscription update failed. Please try again.', {
        title: 'Could not update subscription',
      });
    }, 2000); // 2 second fallback check

    // Set longer timeout to detect if Appstle doesn't respond at all (5 seconds)
    const timeoutId = setTimeout(() => {
      const pending = pendingSubscriptionChanges.get(lineItemKey);
      if (pending) {
        // Final fallback - revert and show error
        pending.select.value = pending.previousValue || 'onetime';
        pending.select.disabled = false;
        delete pending.select.dataset.loading;

        // Remove spinner
        const spinner = pending.select.parentElement?.querySelector(
          'ck-spinner[data-subscription-spinner]'
        );
        if (spinner) spinner.remove();

        pendingSubscriptionChanges.delete(lineItemKey);
        clearTimeout(fallbackTimeoutId);
        activeSyncTimeouts.delete(lineItemKey);
        activeSyncTimeouts.delete(`${lineItemKey}_fallback`);

        window.ckToast?.error('subscription', 'Subscription update failed. Please try again.', {
          title: 'Could not update subscription',
        });
      }
    }, 5000); // 5 second final timeout

    // Store timeouts for cleanup
    activeSyncTimeouts.set(lineItemKey, timeoutId);
    // Store fallback timeout separately (we'll clear it when Appstle event fires)
    if (!activeSyncTimeouts.has(`${lineItemKey}_fallback`)) {
      activeSyncTimeouts.set(`${lineItemKey}_fallback`, fallbackTimeoutId);
    }
  }

  /**
   * Handle Appstle event - subscription switched to a plan
   */
  function handleAppstleSwitchToSubscription(event) {
    const elements = getCartItemElements(event.target);
    if (!elements) {
      return;
    }

    const planId = event.detail?.selling_plan_id || event.detail?.sellingPlanId;
    const lineItemKey = elements.ckSelect.dataset.cartItemKey;

    if (planId && lineItemKey) {
      updateCkSelectValue(elements.ckSelect, String(planId));

      // Clear pending change and refresh cart
      const pending = pendingSubscriptionChanges.get(lineItemKey);
      if (pending) {
        // Clear both timeouts
        clearTimeout(activeSyncTimeouts.get(lineItemKey));
        clearTimeout(activeSyncTimeouts.get(`${lineItemKey}_fallback`));
        activeSyncTimeouts.delete(lineItemKey);
        activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
        pendingSubscriptionChanges.delete(lineItemKey);
      }

      // Refresh cart to reflect Appstle's update
      if (window.refreshCartAfterSubscriptionChange) {
        window.refreshCartAfterSubscriptionChange(lineItemKey, String(planId));
      }
    }
  }

  /**
   * Handle Appstle event - switched to one-time
   */
  function handleAppstleSwitchToOneTime(event) {
    const elements = getCartItemElements(event.target);
    if (!elements) {
      return;
    }

    const lineItemKey = elements.ckSelect.dataset.cartItemKey;
    updateCkSelectValue(elements.ckSelect, 'onetime');

    if (lineItemKey) {
      // Clear pending change and refresh cart
      const pending = pendingSubscriptionChanges.get(lineItemKey);
      if (pending) {
        // Clear both timeouts
        clearTimeout(activeSyncTimeouts.get(lineItemKey));
        clearTimeout(activeSyncTimeouts.get(`${lineItemKey}_fallback`));
        activeSyncTimeouts.delete(lineItemKey);
        activeSyncTimeouts.delete(`${lineItemKey}_fallback`);
        pendingSubscriptionChanges.delete(lineItemKey);
      }

      // Refresh cart to reflect Appstle's update
      if (window.refreshCartAfterSubscriptionChange) {
        window.refreshCartAfterSubscriptionChange(lineItemKey, 'onetime');
      }
    }
  }

  document.addEventListener(
    'AppstleSubscription:SubscriptionWidget:widgetInitialised',
    handleAppstleWidgetInit,
    true
  );

  document.addEventListener(
    'AppstleSubscription:SubscriptionWidget:SwitchedToSubscription',
    handleAppstleSwitchToSubscription,
    true
  );
  document.addEventListener(
    'AppstleSubscription:SubscriptionWidget:SwitchedToOneTime',
    handleAppstleSwitchToOneTime,
    true
  );

  // Cleanup function to clear all pending sync timeouts
  function clearAllSyncTimeouts() {
    activeSyncTimeouts.forEach((timeoutId) => {
      clearTimeout(timeoutId);
    });
    activeSyncTimeouts.clear();
  }

  window.cartSubscription = {
    hydrateSubscriptionSelects,
    getSellingPlanIdFromCartItem,
    getProductHandleFromItem,
    syncAppstleWidgetFromSelect,
    ensureAppstleWidget,
    handleSubscriptionChange,
    clearAllSyncTimeouts,
  };
})();
